import React, { useEffect, useState, useMemo } from 'react';
import { Table } from 'antd';
import moment from 'moment';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { bookingJourneyType, orderStage } from 'common/constants/booking';
import generatePath from '~settings/routing';
import { QUERY_LISTORDERS } from './queries';
import { sortByMapping, sortDirectionMapping } from '~client/constants/index';

const GET_IN_PROGRESS_ORDER_FILTER = {
  bookingJourneys: [bookingJourneyType.SEMI_AUTOMATIC],
  stages: [orderStage.WAITING_FOR_CONFIRMATION, orderStage.WAITING_FOR_PAYMENT],
};

const InprogressBooking = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [inProgressTotal, setInProgressTotal] = useState(0);
  const [processOrders, setProcessOrders] = useState([]);

  const statusFilter = useMemo(() => {
    const inProgressStatus = {
      WAITING_FOR_CONFIRMATION: orderStage.WAITING_FOR_CONFIRMATION,
      WAITING_FOR_PAYMENT: orderStage.WAITING_FOR_PAYMENT,
    };

    return Object.keys(inProgressStatus).map(status => ({
      text: t(`cms.booking.message_property.table.booking_status.${status.toLowerCase()}`),
      value: status.toUpperCase(),
    }));
  }, []);

  const [getInProgressBooking, { loading }] = useLazyQuery(QUERY_LISTORDERS, {
    fetchPolicy: 'no-cache',
    onCompleted: result => {
      if (result?.listOrders?.edges) {
        setProcessOrders(result?.listOrders?.edges?.map(i => ({ ...i?.node })));
        setInProgressTotal(result?.listOrders?.totalCount);
      }
    },
  });

  useEffect(() => {
    getInProgressBooking({
      variables: { ...GET_IN_PROGRESS_ORDER_FILTER },
    });
  }, []);

  const columns = [
    {
      title: t('cms.booking.in_progress.table.landlord_name'),
      key: 'name',
      dataIndex: 'name',
      width: 200,
      render: (_, record) => record?.landlord?.name,
    },
    {
      title: t('cms.booking.in_progress.table.booking_number'),
      dataIndex: 'referenceId',
      key: 'referenceId',
      width: 200,
    },
    {
      title: t('cms.booking.in_progress.table.student_name'),
      dataIndex: 'studentName',
      key: 'studentName',
      width: 200,
      render: (_, record) => record?.student?.fullName || '',
    },
    {
      title: t('cms.booking.in_progress.table.snapshot_student_name'),
      dataIndex: 'passportFullName',
      key: 'passportFullName',
      width: 200,
    },
    {
      title: t('cms.booking.in_progress.table.booking_status'),
      key: 'stages',
      dataIndex: 'stages',
      width: 200,
      render: (_, record) => (
        <div className="transfer__table-field">
          {record?.stage
            ? t(`cms.booking.in_progress.table.booking_status.${record?.stage?.toLowerCase()}`)
            : '-'}
        </div>
      ),
      filters: statusFilter,
    },
    {
      title: t('cms.booking.in_progress.table.create_date'),
      key: 'createdAt',
      dataIndex: 'createdAt',
      sorter: true,
      width: 150,
      render: text => (text ? moment(text).format('DD/MM/YYYY HH:mm:ss') : '-'),
    },
  ];

  const handleOnRowClick = record => {
    const url =
      record?.bookingJourney === bookingJourneyType.SEMI_AUTOMATIC
        ? generatePath('booking.detail', { id: record?.referenceId })
        : generatePath('booking.manualDetail', { id: record?.referenceId });
    history.push(url);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const pageNumber = pagination?.current;
    const pageSize = pagination?.pageSize;
    let newFilter = { pageNumber, pageSize };

    if (sorter?.order) {
      newFilter.sortDirection = sortDirectionMapping[sorter?.order];
      newFilter.sortBy = sortByMapping[sorter?.field];
    }

    if (filters?.stages && filters?.stages.length > 0) {
      newFilter.stages = filters?.stages;
    }

    getInProgressBooking({
      variables: { ...GET_IN_PROGRESS_ORDER_FILTER, ...newFilter },
    });
  };

  return (
    <div className="booking-table-wrapper">
      <Table
        className={classNames('booking__table', {
          'booking__auto-table': processOrders?.length > 0,
        })}
        columns={columns}
        loading={loading}
        dataSource={processOrders}
        style={{ marginTop: '20px' }}
        onChange={handleTableChange}
        pagination={{
          total: inProgressTotal,
          showSizeChanger: true,
        }}
        onRow={record => ({
          onClick: () => handleOnRowClick(record),
        })}
      />
    </div>
  );
};

export default InprogressBooking;
